// US-56 — глобальный maintenance-state.
//
// Идея: api()-хелпер вызывает `enterMaintenance()` при получении 5xx/network
// ошибки; <MaintenanceProvider> подписан на эти события и рендерит full-screen
// overlay. Параллельно overlay сам пингует `/api/health/` каждые 15 сек и
// автоматически снимает maintenance, когда backend возвращается.
//
// Module-level event-emitter специально не использует React-Context, чтобы
// api/client.ts мог дёргать его из любого места без передачи provider'а в
// сетевой слой.

export type MaintenanceReason = "network" | "server" | "crash";

export interface MaintenanceState {
  active: boolean;
  reason: MaintenanceReason | null;
  /** ISO-таймстамп последнего входа в maintenance — для UI «обнаружено в HH:MM». */
  triggeredAt: string | null;
}

type Listener = (state: MaintenanceState) => void;

let currentState: MaintenanceState = {
  active: false,
  reason: null,
  triggeredAt: null,
};

const listeners = new Set<Listener>();

export function getMaintenanceState(): MaintenanceState {
  return currentState;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify() {
  for (const listener of listeners) {
    listener(currentState);
  }
}

export function enterMaintenance(reason: MaintenanceReason): void {
  // Идемпотентно: если уже active с тем же reason — не дёргаем listeners.
  if (currentState.active && currentState.reason === reason) return;
  currentState = {
    active: true,
    reason,
    triggeredAt: new Date().toISOString(),
  };
  notify();
}

export function exitMaintenance(): void {
  if (!currentState.active) return;
  currentState = {
    active: false,
    reason: null,
    triggeredAt: null,
  };
  notify();
}

/** Сброс state для тестов — НЕ использовать в продовом коде.
 * Listeners НЕ трогаем: они привязаны к React-компонентам, React сам
 * отписывает их при unmount. Если очистим — рискуем оборвать активного
 * подписчика прямо во время теста.
 */
export function __resetMaintenanceForTests(): void {
  currentState = { active: false, reason: null, triggeredAt: null };
}
